import DataThresholdingOutlinedIcon from '@mui/icons-material/DataThresholdingOutlined';
import { CircularProgress, DialogContent, Typography } from '@mui/material';
import StdDialog from 'components/Dialogs/StdDialog';
import RegisterOnAfterDate from 'components/Transactions/Register/RegisterOnAfterDate';
import { Set } from 'immutable';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { actionHelpers } from 'app-components/flux/core';
import { preferencesV2Actions, preferencesV2Selectors, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';
import QButton from 'components/QButton';
import { useDispatch, useSelector } from 'react-redux';

import { resetTTI } from 'utils/tti';

import { DIALOG_TYPE_TXN_SETTINGS } from './actions';

const useStyles = makeStyles()((theme) => ({
  description: {
    marginTop: 16,
    marginBottom: 16,
  },
  adviceSurface: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.info.light,
    marginTop: 32,
    borderRadius: 8,
    padding: '8px 16px',
    alignItems: 'center',
  },
  icon: {
    color: theme.palette.info.main,
    marginRight: 8,
  },
  text: {
    color: theme.palette.text.secondary,
  },
  select: {
    width: 300,
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: `${theme.palette.background.default} !important`,
  },
  spaceBetween: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  buttons: {
    alignSelf: 'flex-end',
  },
  button: {
    marginLeft: 16,
  },
  loadingIcon: {
    marginLeft: 4,
    marginRight: 4,
    color: theme.palette.common.white,
  },
}));

function TransactionsSettingsDialog(props: { setSelectedTxnIDs?: (ids: Set<string>) => void }) {
  const { setSelectedTxnIDs } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const dateOnAfterISO: string = useSelector((state) =>
    preferencesV2Selectors.getPreferenceValue(state, 'dateOnAfter'),
  );
  const minDateISO = useSelector(transactionsSelectors.getEarliestDateOn);
  const [tempSelection, setTempSelection] = useState<string | null>(dateOnAfterISO);
  const [loading, setLoading] = useState(false);

  const onSubmit = () => {
    if (tempSelection) {
      tracker.track(tracker.events.transactionsOnDemand, {
        selectionType: 'preference',
        date: tempSelection,
        earliestDate: minDateISO,
        initiator: 'transactions-settings',
      });
      resetTTI();
      dispatch(
        preferencesV2Actions.updatePreferenceOverride(
          preferencesV2Types.mkPreferenceV2({
            id: 'dateOnAfter',
            value: tempSelection,
          }),
        ),
      );

      setLoading(true);
      actionHelpers
        .bindPromiseAction(dispatch, transactionsActions.getTransactions)(undefined, {
          props: { dateOnAfter: tempSelection },
        })
        .then(() => dispatch(rootUiActions.removeDialog(DIALOG_TYPE_TXN_SETTINGS)))
        .finally(() => setLoading(false))
        .catch((error) => assert(false, error));
    }
  };

  const onCancel = () => dispatch(rootUiActions.removeDialog(DIALOG_TYPE_TXN_SETTINGS));

  return (
    <StdDialog size='sm' sharedcomponentid={DIALOG_TYPE_TXN_SETTINGS} title={'Transaction History Settings'} {...props}>
      <DialogContent className={classes.spaceBetween}>
        <div>
          <Typography variant='bodyRegular' className={classes.description}>
            {
              'Choose the duration of transaction history you want to load for all accounts by default in Quicken Simplifi.'
            }
          </Typography>
          <RegisterOnAfterDate
            label={'Default'}
            rootClassName={classes.select}
            controlled={false}
            onChange={(date) => {
              if (date) {
                setTempSelection(date);
              }
              if (setSelectedTxnIDs) {
                setSelectedTxnIDs(Set());
              }
            }}
          />
          <div className={classes.adviceSurface}>
            <DataThresholdingOutlinedIcon fontSize='medium' className={classes.icon} />
            <Typography className={classes.text} variant='captionRegular'>
              {"We advise choosing a shorter duration to enhance the app's performance."}
            </Typography>
          </div>
        </div>
        <div className={classes.buttons}>
          <QButton id='cancel' onClick={onCancel} variant='outlined'>
            {'Cancel'}
          </QButton>

          <QButton
            type='submit'
            id='save-txn-settings'
            variant='contained'
            disabled={tempSelection === dateOnAfterISO || loading}
            className={classes.button}
            onClick={onSubmit}
            startIcon={loading ? <CircularProgress size={16} className={classes.loadingIcon} /> : undefined}
          >
            {'Confirm'}
          </QButton>
        </div>
      </DialogContent>
    </StdDialog>
  );
}

export default TransactionsSettingsDialog;
